import { memo, useCallback, useMemo, useEffect, useState } from 'react';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIPopover from 'UIComponents/tooltip/UIPopover';
import UIPopoverBody from 'UIComponents/tooltip/UIPopoverBody';
import UIPopoverCloseButton from 'UIComponents/tooltip/UIPopoverCloseButton';
import SyntheticEvent from 'UIComponents/core/SyntheticEvent';
import CRMPropertyDateTimeInput from '../../../datetime/CRMPropertyDateTimeInput';
import { getDisplayedValue } from 'customer-data-property-utils/PropertyValueDisplay';
import UIButton from 'UIComponents/button/UIButton';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import Small from 'UIComponents/elements/Small';
import { getTransferrableProps } from '../../../utils/getTransferrableProps';
import UITextInput from 'UIComponents/input/UITextInput';
import emptyFunction from 'react-utils/emptyFunction';
import { useObjectId } from '../../../v2/hooks/useObjectId';
import { usePropertyValidation } from '../../../validation/hooks/usePropertyValidation';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const DateTimePopover = props => {
  const {
    value,
    property,
    objectTypeId,
    onFocus,
    popoverProps,
    popoverOpen,
    setPopoverOpen,
    disableValidation,
    applyChange
  } = props;
  const objectId = useObjectId();
  const [draftValue, setDraftValue] = useState(value);
  const valueDoesNotExist = draftValue === undefined || draftValue == null || draftValue === '';
  const hasValueChanged = value !== draftValue;
  const handleDraftChange = useCallback(changes => {
    const newDatetimeValue = changes[property.name];
    setDraftValue(newDatetimeValue);
  }, [property.name]);
  const resetDraftState = useCallback(() => {
    setDraftValue(value);
  }, [value]);
  const {
    onChangeSingle,
    validationState: {
      saveable,
      invalid: error,
      message: validationMessage,
      loading
    }
  } = usePropertyValidation({
    disableValidation,
    objectId,
    objectTypeId,
    onChange: handleDraftChange,
    // No `onValidationChange` needed because we don't broadcast the result of
    // this validation to our parent component. Validation is entirely internal
    // to this component
    onValidationChange: emptyFunction,
    propertyDefinition: property,
    propertyValue: draftValue,
    validationSettings: {
      validateInitialValue: true
    }
  });
  const isSaveable = !loading && !error && hasValueChanged && saveable;
  const handleChange = useCallback(evt => {
    const {
      target: {
        value: targetValue
      }
    } = evt;
    if (typeof onChangeSingle === 'function') {
      onChangeSingle(targetValue == null ? SyntheticEvent(null) : SyntheticEvent(String(targetValue)));
    }
  }, [onChangeSingle]);
  const closePopoverAndResetState = useCallback(() => {
    setPopoverOpen(false);
    resetDraftState();
  }, [resetDraftState, setPopoverOpen]);
  const saveAndClosePopover = useCallback(() => {
    applyChange(draftValue);
    setPopoverOpen(false);
  }, [applyChange, draftValue, setPopoverOpen]);
  useEffect(() => {
    if (!popoverOpen) {
      resetDraftState();
    }
  }, [popoverOpen, resetDraftState]);

  // We only show the timezone difference message if the user's timezone is different from the portal's timezone
  const userTzDiffersFromPortalTz = useMemo(() => I18n.moment.userTz().zoneName() !== I18n.moment.portalTz().zoneName(), []);
  const renderContent = useCallback(() => {
    return /*#__PURE__*/_jsxs(UIPopoverBody, {
      className: "p-bottom-5",
      children: [/*#__PURE__*/_jsxs(UIFlex, {
        justify: "between",
        children: [/*#__PURE__*/_jsx("b", {
          children: property.label
        }), /*#__PURE__*/_jsx(UIPopoverCloseButton, {
          onClick: closePopoverAndResetState
        })]
      }), /*#__PURE__*/_jsxs(UIFlex, {
        direction: "column",
        className: "m-top-2",
        gap: 12,
        children: [/*#__PURE__*/_jsxs(UIFlex, {
          direction: "column",
          gap: 8,
          children: [/*#__PURE__*/_jsx(CRMPropertyDateTimeInput, Object.assign({}, props, {
            value: draftValue,
            onChange: handleChange,
            error: error
          })), /*#__PURE__*/_jsxs(UIFlex, {
            direction: "column",
            gap: 4,
            children: [userTzDiffersFromPortalTz && error ? /*#__PURE__*/_jsx(Small, {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "customerDataProperties.PropertyInputDateTime.timezone",
                options: {
                  timezone: `${I18n.moment.userTz().format('UTC Z z')}`
                }
              })
            }) : null, error && /*#__PURE__*/_jsx(Small, {
              "data-test-id": "popover-validation-message",
              use: "error",
              children: validationMessage
            })]
          })]
        }), /*#__PURE__*/_jsxs(UIFlex, {
          direction: "row",
          align: "center",
          children: [/*#__PURE__*/_jsx(UIButton, {
            use: "tertiary",
            size: "xs",
            onClick: saveAndClosePopover,
            disabled: !isSaveable,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "customerDataProperties.PropertyInputDateTime.apply"
            })
          }), /*#__PURE__*/_jsx(UIButton, {
            use: "tertiary-light",
            size: "xs",
            onClick: closePopoverAndResetState,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "customerDataProperties.PropertyInputDateTime.cancel"
            })
          }), /*#__PURE__*/_jsx(UIButton, {
            use: "link",
            size: "xs",
            onClick: () => handleChange(SyntheticEvent(null)),
            disabled: valueDoesNotExist,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "customerDataProperties.PropertyInputDateTime.clear"
            })
          })]
        })]
      })]
    });
  }, [closePopoverAndResetState, draftValue, error, handleChange, isSaveable, property.label, props, saveAndClosePopover, userTzDiffersFromPortalTz, validationMessage, valueDoesNotExist]);
  const handleOpenChange = useCallback(evt => {
    setPopoverOpen(evt.target.value);
  }, [setPopoverOpen]);
  const handleFocus = useCallback(evt => {
    if (onFocus) {
      onFocus(evt);
    }
    setPopoverOpen(true);
  }, [onFocus, setPopoverOpen]);
  const formattedValue = useMemo(() => {
    return getDisplayedValue(property, value, undefined, {}, objectTypeId);
  }, [objectTypeId, property, value]);
  return /*#__PURE__*/_jsx(UIPopover, Object.assign({
    width: 400,
    placement: "right"
  }, popoverProps, {
    open: popoverOpen,
    renderContent: renderContent,
    onOpenChange: handleOpenChange,
    closeOnTargetLeave: true,
    "data-test-id": "property-input-date-time-popover",
    children: /*#__PURE__*/_jsx(UITextInput, Object.assign({}, getTransferrableProps(props), {
      value: formattedValue,
      inputRef: props.inputRef,
      onChange: emptyFunction,
      onFocus: handleFocus,
      readOnly: props.readOnly,
      disabled: props.disabled,
      "data-test-id": "property-input-date-time-button"
    }))
  }));
};
export default /*#__PURE__*/memo(DateTimePopover);